import { useEffect, useState } from "react";
import type { EpisodeManifest } from "@scrile/contracts";
import { Avatar } from "../components/Avatar";
import { Icon } from "../components/Icon";
import { t } from "../i18n";

type HeroCard = EpisodeManifest["heroCards"][number];

/** Сколько секунд карточка висит после своего момента, если её не закрыли. */
export const CARD_VISIBLE_SEC = 15;

/** Карточка, которую показать в момент t: последняя наступившая и ещё не истёкшая. */
export function activeCard(cards: readonly HeroCard[], currentTime: number, dismissed: ReadonlySet<string>): HeroCard | null {
  let active: HeroCard | null = null;
  for (const card of cards) {
    const key = `${card.slug}@${card.atSec}`;
    if (dismissed.has(key)) continue;
    if (currentTime >= card.atSec && currentTime < card.atSec + CARD_VISIBLE_SEC) {
      if (!active || card.atSec >= active.atSec) active = card;
    }
  }
  return active;
}

/**
 * Карточка другого героя, всплывающая в момент упоминания. Это ссылка на его поддомен,
 * а не переход сюжета; открывается в новой вкладке — серия здесь продолжает играть.
 */
export function HeroCardPopup({ cards, currentTime, trackKey }: { cards: readonly HeroCard[]; currentTime: number; trackKey: string }) {
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());

  // Новая серия — закрытые карточки прошлой не в счёт
  useEffect(() => setDismissed(new Set()), [trackKey]);

  const card = activeCard(cards, currentTime, dismissed);
  if (!card) return null;
  const key = `${card.slug}@${card.atSec}`;

  // Вся карточка — ссылка: отдельная кнопка «Visit» на телефоне отнимала у имени и слогана
  // половину ширины. Открывается в новой вкладке — серия здесь продолжает играть
  return (
    <aside className="hero-card" role="status" aria-live="polite" aria-label={t("player.heroCard.label")}>
      <a className="hero-card__link" href={card.url} target="_blank" rel="noopener">
        <Avatar src={card.portraitUrl} name={card.name} size={48} />
        <span className="hero-card__text">
          <span className="sr-only">{t("player.heroCard.visit")} </span>
          <span className="hero-card__name">{card.name}</span>
          {card.tagline && <span className="hero-card__tagline">{card.tagline}</span>}
        </span>
        <Icon name="external" size={18} className="hero-card__go" />
      </a>
      <button
        type="button"
        className="icon-btn icon-btn--quiet icon-btn--sm"
        onClick={() => setDismissed(new Set([...dismissed, key]))}
        aria-label={t("app.close")}
      >
        <Icon name="close" size={18} />
      </button>
    </aside>
  );
}
